import { Button, Col, DownloadIcon, Table, TableActionBarRow, ValueOrFallback } from "melco-ui";
import { useTranslation } from "react-i18next";
import {
  FormattedDate,

  IdentityMelcoModelsUserPurchaseListItem,

  Search,

} from "melco-shared-logic";
import { usePurchases } from "../../hooks/billing/usePurchases";
import { useDownloadPurchaseInvoice } from "../../hooks/billing/useDownloadPurchaseInvoice";
import { formatCurrency } from "../../helper/formatCurrency";

export const PurchasesList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
  } = usePurchases();

  const { t } = useTranslation();
  //  const { formatNumber } = useStatisticsNumberFormatter();

  const { downloadPurchaseInvoice, isDownloadingForMelcoPaymentId } =
    useDownloadPurchaseInvoice();

  const columns = [
    {
      title: t("purchases.list.columns.order_id"),
      key: "order_reference_id",
      dataIndex: "order_reference_id",
      sorter: true,
      render: (_: string, item: IdentityMelcoModelsUserPurchaseListItem) => (
        <ValueOrFallback
          value={item.order_reference_id}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("purchases.list.columns.purchase_date"),
      key: "purchase_date",
      dataIndex: "purchase_date",
      sorter: true,
      render: (_: string, item: IdentityMelcoModelsUserPurchaseListItem) => (
        <FormattedDate date={item.purchase_date} />
      ),
    },
    {
      title: t('purchases.list.columns.total_amount'),
      key: 'total_amount',
      dataIndex: 'total_amount',
      sorter: true,
      // Right-aligned so decimal points line up and amounts are easy to compare.
      // align: 'right',
      // The column only wires data in. All formatting rules live in the pure,
      // unit-tested formatCurrency utility (separation of concerns).
      render: (value: string | number, item: IdentityMelcoModelsUserPurchaseListItem) =>
        formatCurrency(value, item.payout_currency),
    },
    {
      title: t("purchases.list.columns.download"),
      key: "download",
      //dataIndex: "actions",
      sorter: false,
      // WHY melco_payment_id is checked before rendering the button at all,
      // rather than just disabling it: the model types this field as
      // `string | null` (see IdentityMelcoModelsUserPurchaseListItem) — the
      // download endpoint needs it as a path segment, so a row that
      // genuinely has none (rather than the field just not having loaded
      // yet) has nothing a download button could ever do.
      //
      // WHY `title` is still set even though there's no visible label: an
      // icon-only button with nothing else identifying it is a screen
      // reader/accessibility gap — `title` gives it a hover tooltip and an
      // accessible name without adding visible column text, so "no text in
      // this column" doesn't have to mean "no label at all" for it.
      render: (_: string, item: IdentityMelcoModelsUserPurchaseListItem) =>
        item.melco_payment_id && (
          <Button
            size="small"
            icon={<DownloadIcon />}
            title={t("purchases.actions.download.button")}
            onClick={(e) => {
              e.stopPropagation();
              void downloadPurchaseInvoice(item.melco_payment_id!);
            }}
            loading={
              isDownloadingForMelcoPaymentId != null &&
              isDownloadingForMelcoPaymentId === item.melco_payment_id
            }
          />
        ),
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col xs={24} xl={17}>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey={(record: IdentityMelcoModelsUserPurchaseListItem, index?: number) =>
          record.melco_payment_id ?? String(index)
        }
        noDataMessage={t("purchases.list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
